import { Link } from 'react-router-dom';
import {
  Heart,
  Clock,
  CalendarDays,
  CheckCircle2,
  AlertCircle,
  FileText,
  Navigation,
  X,
  ExternalLink,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import {
  EVENT_TYPES,
  formatDate,
  formatEventDates,
  formatCurrency,
  getDaysUntil,
} from '../../lib/utils';
import { PRIORITY_COLORS, getEventPriorityType } from './eventPriority';

export const TableView = ({
  events,
  getDistance,
  favoriteIds,
  applicationStatusMap,
  homeState,
}) => {
  const priorityOf = (event) =>
    getEventPriorityType(event, applicationStatusMap, favoriteIds);

  const renderTableRow = (event) => {
    const priorityType = priorityOf(event);
    const eventType =
      EVENT_TYPES[event.event_type] || { label: event.event_type, color: 'gray' };
    const distance = getDistance(event);
    const daysUntilDeadline = getDaysUntil(event.deadline);
    const appStatus = applicationStatusMap[event.id];
    const colors = PRIORITY_COLORS[priorityType] || PRIORITY_COLORS.community;

    return (
      <TableRow
        key={event.id}
        className="hover:bg-[#FFEDDF] relative group"
        data-testid={`table-row-${event.id}`}
      >
        {/* Colored tab */}
        <TableCell className="p-0 w-2 relative">
          <div
            className={`absolute left-0 top-0 bottom-0 w-1.5 ${colors.dot} rounded-r-sm`}
            style={{
              clipPath: 'polygon(0 0, 100% 10%, 100% 90%, 0 100%)',
              boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
            }}
          />
          {priorityType === 'applicationInProgress' && (
            <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-white animate-pulse" />
          )}
          {priorityType === 'accepted' && (
            <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-white" />
          )}
        </TableCell>
        <TableCell className="font-medium text-[#1E3A5F]">
          <div className="flex items-center gap-2">
            {favoriteIds.has(event.id) && (
              <Heart className="w-4 h-4 text-[#FE5F55] fill-[#FE5F55] flex-shrink-0" />
            )}
            <span className="truncate max-w-[200px]">{event.title}</span>
          </div>
        </TableCell>
        <TableCell className="text-[#4A4A4A]">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}
          >
            {eventType.label}
          </span>
        </TableCell>
        <TableCell className="text-[#4A4A4A]">
          {event.city}, {event.state}
        </TableCell>
        <TableCell className="text-[#4A4A4A]">
          {(() => {
            if (event.recurring_schedule?.computed_dates?.length > 0) {
              const sched = event.recurring_schedule;
              const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
              const freqLabels = {
                every: 'Every',
                every_2nd: 'Every 2nd',
                every_3rd: 'Every 3rd',
                '1st_and_3rd': '1st & 3rd',
                '2nd_and_4th': '2nd & 4th',
              };
              const daysStr = (sched.days || []).map((d) => dayNames[d]).join(', ');
              const freqStr = freqLabels[sched.frequency] || 'Every';
              const startStr = new Date(event.date + 'T00:00:00').toLocaleDateString(
                'en-US',
                { month: 'short', day: 'numeric' }
              );
              const endStr = event.end_date
                ? new Date(event.end_date + 'T00:00:00').toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })
                : '';
              return (
                <div className="flex flex-col">
                  <span className="text-emerald-700 font-medium text-xs">
                    {freqStr} {daysStr}
                  </span>
                  <span className="text-xs text-[#71717A]">
                    {startStr} - {endStr}
                  </span>
                  <span className="text-xs text-emerald-600">
                    {sched.computed_dates.length} market days
                  </span>
                </div>
              );
            }
            const dates = formatEventDates(event);
            if (dates.length === 0) return formatDate(event.date);
            if (dates.length === 1) return dates[0];
            return (
              <div className="flex flex-col">
                <span>{dates[0]}</span>
                <span className="text-xs text-[#1E3A5F]">+{dates.length - 1} more</span>
              </div>
            );
          })()}
        </TableCell>
        <TableCell>
          {event.deadline ? (
            <div className="flex items-center gap-1">
              <Clock
                className={`w-4 h-4 ${
                  daysUntilDeadline !== null && daysUntilDeadline < 0
                    ? 'text-rose-500'
                    : daysUntilDeadline !== null && daysUntilDeadline <= 7
                    ? 'text-[#FE5F55]'
                    : 'text-[#71717A]'
                }`}
              />
              <span
                className={
                  daysUntilDeadline !== null && daysUntilDeadline < 0
                    ? 'text-rose-500 line-through'
                    : daysUntilDeadline !== null && daysUntilDeadline <= 7
                    ? 'text-[#FE5F55] font-semibold'
                    : 'text-[#4A4A4A]'
                }
              >
                {formatDate(event.deadline)}
                {daysUntilDeadline !== null && daysUntilDeadline >= 0 && (
                  <span className="text-xs ml-1">({daysUntilDeadline}d)</span>
                )}
                {daysUntilDeadline !== null && daysUntilDeadline < 0 && (
                  <span
                    className="text-xs ml-1 text-rose-500 font-medium no-underline"
                    style={{ textDecoration: 'none' }}
                  >
                    Passed
                  </span>
                )}
              </span>
            </div>
          ) : (
            <span className="text-[#71717A]">—</span>
          )}
        </TableCell>
        <TableCell className="font-semibold text-[#1E3A5F]">
          {formatCurrency(event.booth_cost)}
        </TableCell>
        <TableCell>
          {appStatus ? (
            <Link
              to="/applications"
              data-testid={`app-status-badge-${event.id}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold capitalize
                ${
                  appStatus.status === 'draft'
                    ? 'bg-gray-100 text-gray-700 border border-gray-300'
                    : appStatus.status === 'pending'
                    ? 'bg-purple-100 text-purple-700 border border-purple-300'
                    : appStatus.status === 'submitted'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : appStatus.status === 'accepted'
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-400 ring-1 ring-emerald-300'
                    : appStatus.status === 'waitlisted'
                    ? 'bg-orange-100 text-orange-700 border border-orange-400'
                    : appStatus.status === 'rejected'
                    ? 'bg-rose-100 text-rose-700 border border-rose-400'
                    : 'bg-purple-100 text-purple-700 border border-purple-300'
                }
              `}
            >
              {appStatus.status === 'accepted' && <CheckCircle2 className="w-3 h-3" />}
              {appStatus.status === 'pending' && <Clock className="w-3 h-3" />}
              {appStatus.status === 'draft' && <FileText className="w-3 h-3" />}
              {appStatus.status === 'submitted' && <Navigation className="w-3 h-3" />}
              {appStatus.status === 'waitlisted' && <AlertCircle className="w-3 h-3" />}
              {appStatus.status === 'rejected' && <X className="w-3 h-3" />}
              {appStatus.status}
            </Link>
          ) : (
            <span className="text-[#71717A] text-xs">Not started</span>
          )}
        </TableCell>
        {homeState && (
          <TableCell className="text-[#4A4A4A]">
            {distance && !isNaN(distance) ? `${distance} mi` : '—'}
          </TableCell>
        )}
        <TableCell>
          <Link to={`/events/${event.id}`}>
            <Button variant="ghost" size="sm" className="text-[#1E3A5F]">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </Link>
        </TableCell>
      </TableRow>
    );
  };

  const colSpan = homeState ? 10 : 9;

  // Group ALL events by month first
  const monthGroups = {};
  events.forEach((event) => {
    const dateStr = event.date || '9999-12-31';
    const [y, m] = dateStr.split('-').map(Number);
    const monthKey = `${y}-${String(m).padStart(2, '0')}`;
    if (!monthGroups[monthKey]) {
      const d = new Date(y, m - 1, 1);
      monthGroups[monthKey] = {
        label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        events: [],
      };
    }
    monthGroups[monthKey].events.push(event);
  });

  const sortedMonthKeys = Object.keys(monthGroups).sort();

  return (
    <div className="card-dashboard overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-b-2 border-[#1E3A5F]">
            <TableHead className="w-2"></TableHead>
            <TableHead className="font-bold text-[#1E3A5F]">Event</TableHead>
            <TableHead className="font-bold text-[#1E3A5F]">Type</TableHead>
            <TableHead className="font-bold text-[#1E3A5F]">Location</TableHead>
            <TableHead className="font-bold text-[#1E3A5F]">Date</TableHead>
            <TableHead className="font-bold text-[#1E3A5F]">Deadline</TableHead>
            <TableHead className="font-bold text-[#1E3A5F]">Cost</TableHead>
            <TableHead className="font-bold text-[#1E3A5F]">Application</TableHead>
            {homeState && (
              <TableHead className="font-bold text-[#1E3A5F]">Distance</TableHead>
            )}
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedMonthKeys.map((monthKey) => {
            const group = monthGroups[monthKey];
            const monthEvents = group.events;

            // Within this month, separate into tiers
            const accepted = monthEvents.filter((e) => priorityOf(e) === 'accepted');
            const saved = monthEvents.filter((e) => priorityOf(e) === 'saved');
            const rest = monthEvents.filter((e) => {
              const pt = priorityOf(e);
              return pt !== 'accepted' && pt !== 'saved';
            });

            // Sort rest by event date
            rest.sort((a, b) => {
              const da = a.date || '9999-12-31';
              const db = b.date || '9999-12-31';
              return da.localeCompare(db);
            });

            return [
              <TableRow
                key={`month-hdr-${monthKey}`}
                className="bg-[#1E3A5F]/5 border-y border-[#1E3A5F]/20"
                data-testid={`table-month-header-${monthKey}`}
              >
                <TableCell colSpan={colSpan} className="py-2.5">
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-4 h-4 text-[#1E3A5F]" />
                    <span className="font-bold text-[#1E3A5F] text-sm uppercase tracking-wider">
                      {group.label}
                    </span>
                    <span className="text-xs text-[#1E3A5F]/50 font-medium">
                      ({monthEvents.length} events)
                    </span>
                  </div>
                </TableCell>
              </TableRow>,
              ...accepted.map((e) => renderTableRow(e)),
              ...saved.map((e) => renderTableRow(e)),
              ...((accepted.length > 0 || saved.length > 0) && rest.length > 0
                ? [
                    <TableRow key={`sep-${monthKey}`}>
                      <TableCell colSpan={colSpan} className="p-0">
                        <div className="mx-4 border-t border-dashed border-[#1E3A5F]/20" />
                      </TableCell>
                    </TableRow>,
                  ]
                : []),
              ...rest.map((e) => renderTableRow(e)),
            ];
          })}

          {events.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={colSpan}
                className="text-center py-8 text-[#71717A]"
              >
                No events found. Try adjusting your filters.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default TableView;
